import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';

export const INTERNAL_NETWORK_SCENARIOS_KEY = 'internal-network-scenarios';
export const INTERNAL_NETWORK_CROWN_JEWELS_KEY =
  'internal-network-crown-jewels';

const STALE_TIME_MS = 30_000;

export function useInternalNetworkScenarios({
  signalId,
  enabled = true,
}: {
  signalId: string | null;
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: [INTERNAL_NETWORK_SCENARIOS_KEY, signalId],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkScenariosBySignal({
        signalId: signalId ?? '',
      }),
    enabled: Boolean(signalId) && enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    scenarios: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useStopInternalNetworkScenario({
  signalId,
  networkId,
}: {
  signalId: string | null;
  networkId: string | null;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) =>
      terraProxyApiClient.stopInternalNetworkScenario({ scenarioId }),
    onSuccess: async () => {
      if (signalId) {
        await queryClient.invalidateQueries({
          queryKey: [INTERNAL_NETWORK_SCENARIOS_KEY, signalId],
        });
      }

      if (networkId) {
        await queryClient.invalidateQueries({
          queryKey: [INTERNAL_NETWORK_CROWN_JEWELS_KEY, networkId],
        });
      }
    },
  });
}

export function useInternalNetworkCrownJewels({
  networkId,
  enabled = true,
}: {
  networkId: string | null;
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: [INTERNAL_NETWORK_CROWN_JEWELS_KEY, networkId],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkCrownJewels(networkId ?? ''),
    enabled: Boolean(networkId) && enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    crownJewels: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
